/**
 * Convert decimal degrees to Degrees, Minutes, Seconds (DMS) format
 * @param {number} decimal - Decimal degree value
 * @param {string} type - 'lat' for latitude or 'lng' for longitude
 * @param {number} precision - Number of decimal places for seconds (default: 2)
 * @returns {string} - Formatted DMS string
 */
export const decimalToDMS = (decimal, type, precision = 2) => {
    if (decimal === null || decimal === undefined || isNaN(decimal)) {
        return '';
    }

    const isNegative = decimal < 0;
    const absoluteDecimal = Math.abs(decimal);
    
    // Extract degrees (whole number part)
    let degrees = Math.floor(absoluteDecimal);
    
    // Extract minutes (whole number part of remainder * 60)
    const minutesDecimal = (absoluteDecimal - degrees) * 60;
    let minutes = Math.floor(minutesDecimal);
    
    // Extract seconds (remainder * 60)
    // Use dynamic precision, but ensure it's reasonable (0-6 decimal places)
    const clampedPrecision = Math.max(0, Math.min(6, precision));
    let seconds = ((minutesDecimal - minutes) * 60);
    
    // Handle carry-over for seconds >= 60
    if (Math.round(seconds * Math.pow(10, clampedPrecision)) >= 60 * Math.pow(10, clampedPrecision)) {
        seconds = 0;
        minutes += 1;
        
        // Handle carry-over for minutes >= 60
        if (minutes >= 60) {
            minutes = 0;
            degrees += 1;
        }
    }
    
    const formattedSeconds = seconds.toFixed(clampedPrecision);
    
    // Remove trailing zeros from seconds if precision > 0
    const cleanSeconds = clampedPrecision > 0 ? parseFloat(formattedSeconds).toString() : formattedSeconds;
    
    // Determine cardinal direction
    let direction;
    if (type === 'lat') {
        direction = isNegative ? 'S' : 'N';
    } else if (type === 'lng') {
        direction = isNegative ? 'W' : 'E';
    } else {
        direction = isNegative ? '-' : '+';
    }
    
    return `${degrees}°${minutes}'${cleanSeconds}"${direction}`;
};

/**
 * Convert latitude and longitude to DMS format
 * @param {number} latitude - Latitude in decimal degrees
 * @param {number} longitude - Longitude in decimal degrees
 * @returns {object} - Object with lat and lng in DMS format
 */
export const coordinatesToDMS = (latitude, longitude) => {
    return {
        lat: decimalToDMS(latitude, 'lat'),
        lng: decimalToDMS(longitude, 'lng')
    };
};

/**
 * Format coordinates for display with both decimal and DMS
 * @param {number} latitude - Latitude in decimal degrees
 * @param {number} longitude - Longitude in decimal degrees
 * @returns {string} - Formatted coordinate string
 */
export const formatCoordinatesWithDMS = (latitude, longitude) => {
    const dms = coordinatesToDMS(latitude, longitude);
    return `${latitude}, ${longitude}\n${dms.lat}, ${dms.lng}`;
};
